import { motion, MotionProps } from 'framer-motion';
import { CSSProperties, KeyboardEventHandler, MouseEventHandler } from 'react';
import { QuickstartTheme, getResponsiveHeight } from '../types/theme';

/**
 * Shared logo renderer for Quickstart/Player surfaces.
 *
 * Why this exists: every logo `<img>` site previously set only `height` and
 * relied on the browser to keep the aspect ratio. Tailwind's preflight
 * (`img { max-width: 100%; height: auto; }`) clamps width to the container on
 * narrow viewports while the inline height stays fixed, and the default
 * `<img>` `object-fit` is `fill` — so non-square logos stretched/squished on
 * mobile (issue #12764). This component centralizes the safe styles so all
 * surfaces share one source of truth.
 */
export type ThemeLogoKind = 'studio' | 'game' | 'poweredBy' | 'thankYouStudio';

export interface ThemeLogoProps {
  kind: ThemeLogoKind;
  theme: QuickstartTheme;
  isLandscape?: boolean;
  /**
   * Override the per-kind default height (in CSS px before responsive scaling).
   * Useful for surfaces like PlayNowOverlay that want a larger game logo (112)
   * than the standard default (80).
   */
  defaultHeight?: number;
  className?: string;
  /**
   * Caller-supplied additional style. Aspect-ratio safety props
   * (`width: auto`, `maxWidth: 100%`, `objectFit: contain`) are applied last
   * so they cannot be accidentally clobbered.
   */
  style?: CSSProperties;
  loading?: 'eager' | 'lazy';
  onClick?: MouseEventHandler<HTMLImageElement>;
  onKeyDown?: KeyboardEventHandler<HTMLImageElement>;
  role?: string;
  tabIndex?: number;
  /**
   * If provided, wraps the `<img>` in a `<motion.div>` carrying these props
   * so callers can keep their existing entry/exit animations. Animation runs
   * on the wrapper, not the image, so aspect ratio remains intact.
   */
  motionProps?: MotionProps;
  /** Optional className for the motion wrapper (only applied when motionProps is set). */
  motionClassName?: string;
}

interface ResolvedLogoConfig {
  src: string | undefined;
  alt: string;
  defaultHeight: number;
}

const POWERED_BY_FALLBACK_SRC = '/quickstart/pbp.png';

const resolveLogoConfig = (kind: ThemeLogoKind, theme: QuickstartTheme, defaultHeightOverride?: number): ResolvedLogoConfig => {
  switch (kind) {
    case 'studio':
      return {
        src: theme.studioLogo,
        alt: theme.studioName,
        defaultHeight: defaultHeightOverride ?? theme.studioLogoHeight ?? 64,
      };
    case 'game':
      return {
        src: theme.gameLogo,
        alt: theme.gameName,
        defaultHeight: defaultHeightOverride ?? theme.gameLogoHeight ?? 80,
      };
    case 'poweredBy':
      return {
        src: theme.poweredByLogo || POWERED_BY_FALLBACK_SRC,
        alt: 'Powered by Playcast',
        defaultHeight: defaultHeightOverride ?? theme.poweredByLogoHeight ?? 24,
      };
    case 'thankYouStudio':
      return {
        src: theme.thankYouStudioLogo || theme.studioLogo,
        alt: theme.studioName,
        defaultHeight: defaultHeightOverride ?? theme.studioLogoHeight ?? 64,
      };
  }
};

export const ThemeLogo = ({
  kind,
  theme,
  isLandscape,
  defaultHeight,
  className,
  style,
  loading,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  motionProps,
  motionClassName,
}: ThemeLogoProps) => {
  const { src, alt, defaultHeight: resolvedDefaultHeight } = resolveLogoConfig(kind, theme, defaultHeight);

  if (!src) return null;

  const heightPx = getResponsiveHeight(resolvedDefaultHeight, undefined, isLandscape);

  const mergedStyle: CSSProperties = {
    height: `${heightPx}px`,
    ...style,
    width: style?.width ?? 'auto',
    maxWidth: style?.maxWidth ?? '100%',
    objectFit: style?.objectFit ?? 'contain',
  };

  const imgElement = (
    <img
      src={src}
      alt={alt}
      className={motionProps ? undefined : className}
      style={mergedStyle}
      loading={loading}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
    />
  );

  if (motionProps) {
    return (
      <motion.div className={motionClassName ?? className} {...motionProps}>
        {imgElement}
      </motion.div>
    );
  }

  return imgElement;
};
